import _ from "lodash";
import { Form } from "formik";
import React from "react";
import { useHistory } from "react-router-dom";

import { api } from "@/api";
import { Button2 } from "@/ui2/atoms/Button";
import { cn } from "@/lib/helpers/cn";
import { CustomForm } from "@/component-library";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import dayjs from "@/lib/helpers/dayjs";
import { Divider2 } from "@/ui2/atoms/Divider";
import { filterTimePickerOptionsByLabel } from "@/features/calendar/utils/dates";
import { FormDateField } from "@/component-library/FormFields/FormDateField/FormDateField";
import { FormMultiSelect } from "@/component-library/FormFields/FormMultiSelect/FormMultiSelect";
import { FormRadioGroup } from "@/component-library/FormFields/FormRadioGroup/FormRadioGroup";
import { FormSelect } from "@/component-library/FormFields/FormSelect/FormSelect";
import { FormTextInput } from "@/component-library/FormFields/FormTextInput/FormTextInput";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { ScrollToFormError } from "@/component-library/ScrollToFormError/ScrollToError";

import { CareManagementCredentialField } from "../CareManagement/CareManagementCredentialField";
import { CareManagementSignatureField } from "../CareManagement/CareManagementSignatureField";

import {
  ACTIVITY_CLASS_OPTIONS,
  ACTIVITY_PURPOSE_OPTIONS,
  ACTIVITY_REASON_OPTIONS,
  ACTIVITY_SEVICE_TYPE_OPTIONS,
  ACP_NOTE_FIELDS_CAST_TO_BOOLEAN,
  CARE_TEAM_MEMBER_TYPE_OPTIONS,
  COMMUNICATION_TYPE_OPTIONS,
  CONDITIONAL_FIELD_CLEARING_RULES,
  INTENDED_DURATION_OPTIONS,
  REASON_FOR_COMMUNICATION_OPTIONS,
  RECOMMENDED_NEXT_OPTIONS,
  TIME_SLOT_OPTIONS,
  YES_NO_OPTIONS,
  filterDurationOptionsByLabel,
} from "./constants";
import { ADVANCED_CARE_ACTIVITY_SCHEMA } from "./schema";
import { AdvancedCareActivityTextArea } from "./AdvancedCareActivityTextArea";
import { AdvancedCareActivityTextInput } from "./AdvancedCareActivityInput";

export function AdvancedCareActivityForm({ isEditing, nameToSign, noteState, patientId }) {
  const history = useHistory();

  const preventEnterFormSubmission = (event) => {
    if (event.key === "Enter" || event.code === "Enter") {
      event.preventDefault();
    }
  };

  const renderForm = (formikProps) => {
    return (
      <Form
        onKeyDown={preventEnterFormSubmission}
        className="tw:!grid tw:items-start tw:gap-8 tw:max-lg:grid-cols-1 tw:lg:grid-cols-2"
      >
        <FormSelect
          disabled={!isEditing}
          dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
          formikProps={formikProps}
          name="content.activity_service_type"
          options={ACTIVITY_SEVICE_TYPE_OPTIONS}
          placeholder="Choose most relevant"
          title="Advanced Care Service"
          triggerErrorOnChange
          testId="advanced-care-activity-assessment-activity-service-type-field"
        />

        <div className="tw:flex tw:flex-col tw:gap-5">
          <FormMultiSelect
            disabled={!isEditing}
            dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
            formikProps={formikProps}
            name="content.activity_purpose"
            options={ACTIVITY_PURPOSE_OPTIONS}
            placeholder="Search Activity Type"
            title="Activity Type"
            triggerErrorOnChange
            testId="advanced-care-activity-assessment-activity-purpose-field"
          />

          {formikProps.values.content.activity_purpose.includes("Other") && (
            <AdvancedCareActivityTextInput
              formikProps={formikProps}
              name="content.activity_purpose_other_details"
              readOnly={!isEditing}
              title="Specify Other"
              triggerErrorOnChange
              testId="advanced-care-activity-assessment-activity-purpose-other-field"
            />
          )}
        </div>

        <FormSelect
          disabled={!isEditing}
          dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
          filterOptions={filterDurationOptionsByLabel}
          formikProps={formikProps}
          name="content.intended_duration"
          options={INTENDED_DURATION_OPTIONS}
          placeholder="Anticipated duration of the activity"
          title="Intended Duration"
          triggerErrorOnChange
          testId="advanced-care-activity-assessment-duration-field"
        />

        <FormRadioGroup
          title="Activity Location"
          name="service_location"
          options={ACTIVITY_CLASS_OPTIONS}
          formikProps={formikProps}
          triggerErrorOnChange
          disabled={!isEditing}
          radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
          radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
          testId="advanced-care-activity-assessment-location-field"
        />

        <div className="AdvancedCareActivityForm-additional_question_wrapper">
          <FormSelect
            dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
            disabled={!isEditing}
            title="Communication Method"
            placeholder="Search Communication Method"
            name="content.communication_type"
            options={COMMUNICATION_TYPE_OPTIONS}
            formikProps={formikProps}
            triggerErrorOnChange
            testId="advanced-care-activity-assessment-communication-type-field"
          />

          {formikProps.values.content.communication_type === "Other" && (
            <AdvancedCareActivityTextInput
              title="Specify Other"
              name="content.communication_type_other_details"
              readOnly={!isEditing}
              formikProps={formikProps}
              triggerErrorOnChange
              testId="advanced-care-activity-assessment-communication-type-other-field"
            />
          )}
        </div>

        <FormRadioGroup
          title="Was the activity planned?"
          name="content.activity_reason"
          options={ACTIVITY_REASON_OPTIONS}
          formikProps={formikProps}
          triggerErrorOnChange
          disabled={!isEditing}
          radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
          radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
          testId="advanced-care-activity-assessment-activity-reason-field"
        />

        <FormRadioGroup
          title="Was the patient expected to be present?"
          name="content.patient_encounter"
          options={YES_NO_OPTIONS}
          formikProps={formikProps}
          triggerErrorOnChange
          disabled={!isEditing}
          radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
          radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
          testId="advanced-care-activity-assessment-encounter-field"
        />

        <FormRadioGroup
          title="Did the patient attend?"
          name="content.patient_participant_attest"
          options={YES_NO_OPTIONS}
          formikProps={formikProps}
          triggerErrorOnChange
          disabled={!isEditing}
          radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
          radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
          testId="advanced-care-activity-assessment-attend-field"
        />

        <FormDateField
          title="Activity Date"
          placeholder="MM/DD/YYYY"
          name="session_date"
          formikProps={formikProps}
          triggerErrorOnChange
          inputClassName={!isEditing && "AdvancedCareActivityForm-input_read_only"}
          disabled={!isEditing}
          {...getAutomationDataAttr("advanced-care-activity-assessment-date-field")}
        />

        <FormSelect
          dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
          disabled={!isEditing}
          title="Start Time"
          placeholder="HH:MM"
          name="session_from"
          options={TIME_SLOT_OPTIONS}
          filterOptions={filterTimePickerOptionsByLabel}
          formikProps={formikProps}
          otherErrorNames={["time_error", "start_date_time_error"]}
          triggerErrorOnChange
          testId="advanced-care-activity-assessment-start-field"
        />

        <FormSelect
          dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
          disabled={!isEditing}
          title="End Time"
          placeholder="HH:MM"
          name="session_to"
          options={TIME_SLOT_OPTIONS}
          filterOptions={filterTimePickerOptionsByLabel}
          formikProps={formikProps}
          otherErrorNames={["time_error", "end_date_time_error"]}
          triggerErrorOnChange
          testId="advanced-care-activity-assessment-end-field"
        />

        <FormMultiSelect
          dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
          disabled={!isEditing}
          title="Reason for Communication"
          placeholder="Search Reason for Communication"
          name="content.reason_for_communication"
          options={REASON_FOR_COMMUNICATION_OPTIONS}
          formikProps={formikProps}
          triggerErrorOnChange
          testId="advanced-care-activity-assessment-reason-for-communication-field"
        />

        <div className="AdvancedCareActivityForm-additional_question_wrapper">
          <FormRadioGroup
            className={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
            disabled={!isEditing}
            formikProps={formikProps}
            name="content.activity_result"
            options={YES_NO_OPTIONS}
            radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
            radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
            radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
            title="Was the activity completed as planned?"
            triggerErrorOnChange
            testId="advanced-care-activity-assessment-result-field"
          />

          {formikProps.values.content.activity_result === "No" && (
            <AdvancedCareActivityTextInput
              formikProps={formikProps}
              name="content.activity_result_note"
              readOnly={!isEditing}
              title="Why was the activity not completed as planned?"
              triggerErrorOnChange
              testId="advanced-care-activity-assessment-result-note-field"
            />
          )}
        </div>

        <div className="AdvancedCareActivityForm-additional_question_wrapper">
          <FormMultiSelect
            dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
            disabled={!isEditing}
            title="Care Team Follow Up"
            placeholder="Search Care team follow up"
            name="content.recommended_next"
            options={RECOMMENDED_NEXT_OPTIONS}
            formikProps={formikProps}
            triggerErrorOnChange
            testId="advanced-care-activity-assessment-recommended-next-field"
          />

          {formikProps.values.content.recommended_next.includes("Other") && (
            <AdvancedCareActivityTextInput
              title="Specify Other"
              name="content.recommended_next_other_details"
              formikProps={formikProps}
              triggerErrorOnChange
              readOnly={!isEditing}
              testId="advanced-care-activity-assessment-recommended-next-other-field"
            />
          )}
        </div>

        <Divider2 className="tw:lg:col-span-2" />

        {/* ACUTE EPISODE */}
        <FormRadioGroup
          className={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          disabled={!isEditing}
          formikProps={formikProps}
          name="content.acute_episode"
          options={YES_NO_OPTIONS}
          radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
          radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
          title="ED visit or inpatient care for BH within 6 months?"
          triggerErrorOnChange
          testId="advanced-care-activity-assessment-acute-episode"
        />

        <AdvancedCareActivityTextArea
          formikProps={formikProps}
          isDisabled={
            !formikProps.values.content.acute_episode ||
            formikProps.values.content.acute_episode === "No"
          }
          name="content.acute_episode_details"
          placeholder="Please provide details"
          readOnly={!isEditing}
          title="Details"
          triggerErrorOnChange
          testId="advanced-care-activity-assessment-acute-episode-details"
        />

        {/* ACTIVE SUBSTANCE */}
        <FormRadioGroup
          className={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          disabled={!isEditing}
          formikProps={formikProps}
          name="content.active_substance"
          options={YES_NO_OPTIONS}
          radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
          radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
          title="Active substance use disorder?"
          triggerErrorOnChange
          testId="advanced-care-activity-assessment-active-substance"
        />

        <AdvancedCareActivityTextArea
          formikProps={formikProps}
          isDisabled={
            !formikProps.values.content.active_substance ||
            formikProps.values.content.active_substance === "No"
          }
          name="content.active_substance_details"
          placeholder="Include type of substance and frequency if comfortable sharing"
          readOnly={!isEditing}
          title="Details"
          triggerErrorOnChange
          testId="advanced-care-activity-assessment-active-substance-details"
        />

        {/* SDOH */}
        <FormRadioGroup
          className={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          disabled={!isEditing}
          formikProps={formikProps}
          name="content.sdoh"
          options={YES_NO_OPTIONS}
          radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
          radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
          title="Housing, Financial, or Safety Concerns (SDOH)?"
          triggerErrorOnChange
          testId="advanced-care-activity-assessment-sdoh"
        />

        <AdvancedCareActivityTextArea
          formikProps={formikProps}
          isDisabled={!formikProps.values.content.sdoh || formikProps.values.content.sdoh === "No"}
          name="content.sdoh_details"
          placeholder="Please provide details"
          readOnly={!isEditing}
          title="Details"
          triggerErrorOnChange
          testId="advanced-care-activity-assessment-sdoh-details"
        />

        {/* DAILY ACTIVITIES */}
        <FormRadioGroup
          className={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          disabled={!isEditing}
          formikProps={formikProps}
          name="content.activity_difficulties"
          options={YES_NO_OPTIONS}
          radioButtonClassName={!isEditing && "AdvancedCareActivityForm-radio_button_read_only"}
          radioContainerClassName={!isEditing && "AdvancedCareActivityForm-radio_group_read_only"}
          radioLabelClassName={!isEditing && "AdvancedCareActivityForm-radio_label_read_only"}
          title="Difficulty with Activities of Daily Living (ADLs)?"
          triggerErrorOnChange
          testId="advanced-care-activity-assessment-daily-activities"
        />

        <AdvancedCareActivityTextArea
          formikProps={formikProps}
          isDisabled={
            !formikProps.values.content.activity_difficulties ||
            formikProps.values.content.activity_difficulties === "No"
          }
          name="content.activity_difficulties_details"
          placeholder="Please provide details (cleaning, cooking, toileting, etc.)"
          readOnly={!isEditing}
          title="Details"
          triggerErrorOnChange
          testId="advanced-care-activity-assessment-daily-activities-details"
        />

        <Divider2 className="tw:lg:col-span-2" />

        {/* SUMMARY */}
        <div className="tw:lg:col-span-2">
          <AdvancedCareActivityTextArea
            className="AdvancedCareActivityForm-summary_form_field"
            formikProps={formikProps}
            inputClassName="AdvancedCareActivityForm-summary_form_field_input"
            name="content.additional_information"
            readOnly={!isEditing}
            placeholder="Highlight interventions and patient reactions"
            title="Summary"
            triggerErrorOnChange
            testId="advanced-care-activity-assessment-summary-field"
          />
        </div>

        {/* YOUR ROLE */}
        <div className="AdvancedCareActivityForm-additional_question_wrapper">
          <FormSelect
            disabled={!isEditing}
            dropDownClassName={!isEditing && "AdvancedCareActivityForm-select_read_only"}
            formikProps={formikProps}
            name="content.care_team_member_type"
            options={CARE_TEAM_MEMBER_TYPE_OPTIONS}
            placeholder="Search Role"
            title="Your Role"
            triggerErrorOnChange
            testId="advanced-care-activity-assessment-role-field"
          />

          {formikProps.values.content.care_team_member_type === "Other" && (
            <AdvancedCareActivityTextInput
              formikProps={formikProps}
              name="content.care_team_member_type_other_details"
              readOnly={!isEditing}
              title="Specify Other"
              triggerErrorOnChange
              testId="advanced-care-activity-assessment-role-other-field"
            />
          )}
        </div>

        {/* SIGNATURE/DATE/CREDENTIALS */}
        {/* Copied from CareManagementForm  */}
        <div className="tw:col-start-1 tw:grid tw:gap-5 tw:lg:col-span-2 tw:lg:grid-cols-3">
          {isEditing && (
            <CareManagementSignatureField
              formikProps={formikProps}
              isViewOnly={false}
              nameToSign={nameToSign}
              name="signature"
              testId="advanced-care-activity-assessment-signature-field"
            />
          )}

          {!isEditing && (
            <CareManagementSignatureField
              formikProps={formikProps}
              isViewOnly={true}
              name="signature"
              nameToSign={nameToSign}
              testId="advanced-care-activity-assessment-signature-field"
            />
          )}

          {/* DATE */}
          {!isEditing && (
            <div className="tw:lg:flex tw:lg:justify-center">
              <FormTextInput
                formikProps={formikProps}
                inputClassName={!isEditing && "AdvancedCareActivityForm-input_read_only"}
                name="signature_date"
                readOnly={!isEditing}
                title="Signature Date"
                testId="advanced-care-activity-assessment-signature-date-field"
              />
            </div>
          )}

          {/* CREDENTIALS */}
          <div className={cn("tw:flex tw:lg:justify-end", isEditing && "tw:lg:col-start-3")}>
            {isEditing && (
              <>
                {formikProps.values["provider_cp_credentials"] && (
                  <CareManagementCredentialField
                    formikProps={formikProps}
                    isViewOnly={false}
                    name="provider_cp_credentials"
                  />
                )}
              </>
            )}

            {!isEditing && (
              <>
                {formikProps.values["provider_cp_credentials"] && (
                  <CareManagementCredentialField
                    formikProps={formikProps}
                    isViewOnly={true}
                    name="provider_cp_credentials"
                    testId="advanced-care-activity-assessment-credentials-field"
                  />
                )}
              </>
            )}
          </div>

          <ScrollToFormError />
        </div>

        {/* SUBMIT */}
        {isEditing && (
          <Button2
            className="tw:min-w-50 tw:lg:col-start-2 tw:lg:!ml-auto"
            type="submit"
            {...getAutomationDataAttr("advanced-care-activity-assessment-submit-button")}
          >
            Save Assessment
          </Button2>
        )}
      </Form>
    );
  };

  const onSubmit = async (formData) => {
    const copyOfFormData = _.cloneDeep(formData);
    const { content } = copyOfFormData;

    // Clear conditional details fields based on their parent field values
    CONDITIONAL_FIELD_CLEARING_RULES.forEach(({ detailsField, shouldClear }) => {
      if (shouldClear(content)) {
        copyOfFormData.content[detailsField] = "";
      }
    });

    ACP_NOTE_FIELDS_CAST_TO_BOOLEAN.forEach((item) => {
      const foundOption = YES_NO_OPTIONS.find(
        (option) => option.value === copyOfFormData.content[item],
      );

      copyOfFormData.content[item] = foundOption.booleanValue;
    });

    copyOfFormData["session_date"] = dayjs(copyOfFormData["session_date"], "MMDDYYYY").format(
      "MM/DD/YYYY",
    );

    copyOfFormData["content"]["intended_duration"] = Number(
      copyOfFormData["content"]["intended_duration"],
    );

    copyOfFormData["completed_date_time"] = dayjs().format();

    const urlParams = { patientId };

    await api.provider.post_progress_note({ urlParams, data: copyOfFormData });
    history.push(`/app/patient/${patientId}/profile/v2?selectedTab=Notes`);
  };

  return (
    <DashboardCard2
      title="Advanced Care Activity Assessment"
      {...getAutomationDataAttr("advanced-care-activity-assessment-title")}
    >
      {noteState && (
        <CustomForm
          initialValues={noteState}
          onSubmit={onSubmit}
          render={renderForm}
          validateOnBlur={true}
          validateOnChange={true}
          validationSchema={ADVANCED_CARE_ACTIVITY_SCHEMA}
        />
      )}
    </DashboardCard2>
  );
}
